import { useEffect, useRef, useState } from 'react'

import { Helmet } from 'react-helmet'
import NavBar from 'components/navbar/NavBar'
import Header from 'components/typography/Header'
import Plan from 'components/pricing/Plan'
import styles from './styles/Pricing.css'
import Link from 'components/misc/Link'
import * as analytics from 'lib/analytics'
import Paragraph from '../components/typography/Paragraph'
import { useSelector } from 'react-redux'
import Endorsements from 'components/pricing/Endorsements'
import TabbedView from '../components/misc/TabbedView'
import { LeadGenForm } from '../components/pricing/LeadGenForm'
import PlanTable from '../components/pricing/PlanTable'
import { useLocation } from 'react-router-dom'
import qs from 'query-string'
import { errorToast } from '../lib/toast'
import loginModalStore from 'store/login-modal'
import RedeemCode from '../components/pricing/RedeemCode'
import injectAsyncStore from 'lib/inject-async-store'

const DESCRIPTION_COPY = `
Simple and transparent pricing
Search and discover millions of citations showing expert insights and analyses on any topic and any publication.
`

const IndividualPlans = ({ user, setTab }) => (
  <>
    <PlanTable />
    {user?.plan === 'free' && !user?.trialActivated && (
      <Paragraph>
        After your free trial ends, you'll be automatically enrolled in your chosen plan. You can change or cancel your subscription during your trial period.
      </Paragraph>
    )}
    <Endorsements className={styles.endorsements} />
    <div className={styles.faq}>
      <img alt='recommend institution' width='76px' height='76px' src={`${CONFIG.assetsBaseURL}/images/icons/institution.svg`} />
      <div className={styles.specialOfferSubheading}>
        <Header className={styles.faqHeader} level={4} center>Help & FAQ</Header>
        <Paragraph className={styles.faqParagraph} type='small' light center>
          If you have any problems with your subscription feel free to contact us at <Link blue underline href='mailto:support@scite.ai'>support@scite.ai</Link> anytime and we will get right back to you.
          All prices in USD. Want us to issue an invoice? <Link blue underline href='mailto:support@scite.ai'>contact us</Link>
        </Paragraph>
        <Header level={5} className={styles.faqHeader}>Do you offer group plans or discounts?</Header>
        <Paragraph className={styles.faqParagraph} type='small' light>
          We do! If you are interested in a group plan, please contact us at <Link blue underline href='mailto:sales@scite.ai'>sales@scite.ai</Link> for a quote. Or <Link blue underline onClick={() => setTab('organizations')}>see our group plans for more details.</Link>
        </Paragraph>
        <Header level={5} className={styles.faqHeader}>Do you offer student or academic discounts?</Header>
        <Paragraph className={styles.faqParagraph} type='small' light>
          We do offer discounts for students and academics if they recommend their institution purchase a scite license and cc us or forward the email to <Link blue underline href='mailto:sales@scite.ai'>sales@scite.ai</Link>
        </Paragraph>
        <Header level={5} className={styles.faqHeader}>How do your payments work?</Header>
        <Paragraph className={styles.faqParagraph} type='small' light>
          We use stripe to manage our payments, we do not store or manage credit card information.
        </Paragraph>
      </div>
    </div>
  </>
)

const OrganizationPlans = () => {
  const scrollRef = useRef(null)
  const handleSeeMore = () => scrollRef?.current?.scrollIntoView()
  return (
    <>
      <div className={styles.plansLayout}>
        <Plan plan='organization' handleSeeMore={handleSeeMore} className={styles.orgPlan} />
        <Plan plan='data' handleSeeMore={handleSeeMore} className={styles.orgPlan} />
      </div>
      <Endorsements
        className={styles.endorsements}
        subheader='Join leading organizations like University of Hong Kong and Cold Spring Harbor Laboratories in providing their users with the power to understand what is said about publications and their findings by other researchers'
      />
      <div className={styles.specialOffer}>
        <img alt='recommend institution' width='76px' height='76px' src={`${CONFIG.assetsBaseURL}/images/icons/institution.svg`} />
        <Header className={styles.specialOfferHeader} level={3}>Start an organization-wide trial</Header>
        <div className={styles.specialOfferSubheading}>
          We offer a no obligation free organization-wide trial which will provide scite premium to all your users. <Link blue to={CONFIG.bookDemoURL}>Click here</Link> to get started with a trial.
        </div>
        <div className={styles.specialOffer}>
          <Header className={styles.specialOfferHeader} level={3}>Request a quote or a demo</Header>
          <div className={styles.specialOfferSubheading}>
            scite pricing is based on the number of students and faculty at your institution. We offer organization-wide annual licenses with volume-based discounts. Contact us at <Link blue href='mailto:sales@scite.ai'>sales@scite.ai</Link> to request a quote or get a demo.
          </div>
        </div>
        <div ref={scrollRef} className={styles.specialOffer}>
          <Header className={styles.specialOfferHeader} level={3}>Want to know more?</Header>
          <div>
            <ul>
              <li>See how we help <Link blue underline to='/partners/academic-institutions'>Universities & Academic Institutions</Link></li>
              <li>See how we help <Link blue underline to='/partners/pharmaceuticals-and-biotech'>Pharma & Biotech</Link></li>
              <li>See how we help <Link blue underline to='/parters/publishers'>Publishers</Link></li>
              <li><Link blue underline to='/apiterms'>Get started with integration</Link></li>
            </ul>
            Checkout <Link blue underline to='/partners'>how we help our partners</Link> or <Link blue underline to='/data-and-services'>see our coverage</Link>
          </div>
        </div>
        <LeadGenForm />
      </div>
    </>
  )
}

export const Pricing = () => {
  const location = useLocation()

  const user = useSelector(({ ui }) => ui.user)
  const [tab, setTab] = useState('individuals')
  const [error, setError] = useState()
  const [redeemCodePage, setRedeemCodePage] = useState(false)

  useEffect(() => {
    const query = qs.parse(location.search)
    if (query.tab) {
      setTab(query.tab)
    }

    if (query.redeem) {
      setRedeemCodePage(true)
    }

    if (query.error) {
      let errorCopy = 'Something went wrong. Please try again or contact us at support@scite.ai'
      if (query.error === 'no-user') {
        errorCopy = 'You must be signed in to purchase a plan. If this continues to happen please contact us at support@scite.ai'
      } else if (query.error === 'select-price') {
        errorCopy = 'Please select a price to purchase for. If this continues to happen please contact us at support@scite.ai'
      }
      setError(errorCopy)
    }
  }, [])

  useEffect(() => {
    analytics.event({
      category: 'Pricing',
      action: 'View Pricing Page',
      label: tab
    })
    window.scrollTo(0, 0)
  }, [tab])

  useEffect(() => {
    if (error) {
      errorToast({
        message: error,
        durationMs: 5500
      })
    }
  }, [error])

  return (
    <div className={styles.container}>
      <Helmet>
        <title>scite pricing</title>

        <meta property='title' content='scite pricing' />
        <meta property='og:title' content='scite pricing' />
        <meta property='twitter:title' content='scite pricing' />

        <meta name='description' content={DESCRIPTION_COPY} />
        <meta property='og:description' content={DESCRIPTION_COPY} />
        <meta property='twitter:description' content={DESCRIPTION_COPY} />

        <meta property='og:image' content='http://image.thum.io/get/ogImage/https://scite.ai/pricing?bannerClosed=true&v=2' />
        <meta property='twitter:image' content='http://image.thum.io/get/ogImage/https://scite.ai/pricing?bannerClosed=true&v=2' />

      </Helmet>
      <NavBar staticPage homePage />
      <div className={styles.pricingPageLayout}>
        <Header level={3} className={styles.header} center>
          Simple and transparent pricing
        </Header>
        <Paragraph center className={styles.pricingSubheading}>
          Search over a billion citation statements to discover expert insights and analyses on any topic and any publication.
        </Paragraph>
        {!redeemCodePage
          ? (
            <TabbedView
              className={styles.tabbedView}
              viewClassName={styles.tabbedViewView}
              tabBarClassName={styles.tabBarView}
              tabClassName={styles.tab}
              tabSelectedClassName={styles.tabSelected}
              selected={tab}
              onSelected={(id, name) => setTab(name)}
              tabs={[
                {
                  id: 'individuals',
                  label: 'Individuals',
                  view: () => <IndividualPlans user={user} setTab={setTab} />
                },
                {
                  id: 'organizations',
                  label: 'Enterprise',
                  view: () => <OrganizationPlans />
                }
              ]}
            />
            )
          : (
            <div>
              <Header level={5} center>
                Redeem your code to enable your scite plan
              </Header>
              <Paragraph center>
                Enter the code you have received from a scite partner below to activate your scite plan.
              </Paragraph>
              <RedeemCode />
            </div>
            )}
      </div>
    </div>
  )
}

export default injectAsyncStore({
  loginModal: loginModalStore
})(Pricing)
